import { forwardRef } from 'react'
import { ArrowRight, CornerDownLeft, FileSearch, RefreshCw, SearchX } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/components/ui/empty'
import { Kbd } from '@/components/ui/kbd'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { cn } from '@/lib/utils'
import type { Form } from '../types'
import { ICONS } from './icons'
import { categoryIcon, cleanDescription, groupByCategory, iconFor } from './model'

interface CardProps {
    form: Form
    onStart: (f: Form) => void
    starting: string | null
    /** Marks the card Enter in the search box would start. */
    highlighted?: boolean
}

/** A whole-card "start" button: icon, name, short description and a Start affordance. */
export const FormStartCard = forwardRef<HTMLButtonElement, CardProps>(function FormStartCard(
    { form, onStart, starting, highlighted },
    ref,
) {
    const Icon = ICONS[iconFor(form)]
    const pending = starting === form.id
    const busy = starting !== null
    return (
        <button
            ref={ref}
            type="button"
            data-form-card
            onClick={() => onStart(form)}
            disabled={busy}
            aria-busy={pending || undefined}
            aria-label={`Start ${form.name}`}
            aria-describedby={cleanDescription(form) ? `form-desc-${form.id}` : undefined}
            className={cn(
                'group/card relative flex h-full min-h-36 w-full flex-col gap-3 rounded-xl border bg-card p-4 text-left text-card-foreground shadow-xs outline-none',
                'transition-[border-color,box-shadow,transform] duration-200 motion-reduce:transition-none',
                'hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md motion-reduce:hover:translate-y-0',
                'focus-visible:border-primary focus-visible:ring-3 focus-visible:ring-ring/40',
                'disabled:cursor-progress disabled:hover:translate-y-0',
                busy && !pending && 'opacity-60',
                highlighted && 'border-primary/60 ring-3 ring-ring/20',
            )}
        >
            <div className="flex items-start gap-3">
                <span
                    aria-hidden
                    className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-colors group-hover/card:bg-primary group-hover/card:text-primary-foreground motion-reduce:transition-none"
                >
                    <Icon className="size-5" />
                </span>
                <span className="line-clamp-2 pt-0.5 text-[0.95rem] leading-snug font-semibold">{form.name}</span>
            </div>
            {cleanDescription(form) && (
                <span id={`form-desc-${form.id}`} className="line-clamp-2 text-sm text-muted-foreground">
                    {cleanDescription(form)}
                </span>
            )}
            <span className="mt-auto flex items-center justify-between gap-2 pt-1 text-sm font-medium text-primary">
                <span className="flex items-center gap-1.5">
                    {pending ? <Spinner /> : null}
                    {pending ? 'Starting…' : 'Start'}
                    {!pending && (
                        <ArrowRight aria-hidden className="size-4 transition-transform group-hover/card:translate-x-0.5 motion-reduce:transition-none" />
                    )}
                </span>
                {highlighted && !pending && (
                    <Kbd aria-hidden className="hidden sm:inline-flex">
                        <CornerDownLeft />
                    </Kbd>
                )}
            </span>
        </button>
    )
})

const grid = 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4'

export function CatalogSkeleton() {
    return (
        <div className="flex flex-col gap-8" aria-hidden>
            {[4, 3].map((n, s) => (
                <div key={s} className="flex flex-col gap-3">
                    <Skeleton className="h-4 w-48" />
                    <div className={grid}>
                        {Array.from({ length: n }, (_, i) => <Skeleton key={i} className="h-36 rounded-xl" />)}
                    </div>
                </div>
            ))}
        </div>
    )
}

interface CatalogProps {
    forms: Form[] | null
    error: string | null
    onRetry: () => void
    /** Forms already filtered and ranked by the search (null = no active search). */
    results: Form[] | null
    query: string
    onClearQuery: () => void
    onStart: (f: Form) => void
    starting: string | null
}

export function FormCatalog({ forms, error, onRetry, results, query, onClearQuery, onStart, starting }: CatalogProps) {
    if (forms === null) return <CatalogSkeleton />

    if (error) {
        return (
            <Empty className="border border-dashed">
                <EmptyHeader>
                    <EmptyMedia variant="icon"><FileSearch /></EmptyMedia>
                    <EmptyTitle>We couldn’t load the forms</EmptyTitle>
                    <EmptyDescription>{error}</EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                    <Button variant="outline" onClick={onRetry}><RefreshCw data-icon="inline-start" />Try again</Button>
                </EmptyContent>
            </Empty>
        )
    }

    if (forms.length === 0) {
        return (
            <Empty className="border border-dashed">
                <EmptyHeader>
                    <EmptyMedia variant="icon"><FileSearch /></EmptyMedia>
                    <EmptyTitle>No forms yet</EmptyTitle>
                    <EmptyDescription>Nothing has been published for you to submit. Check back soon.</EmptyDescription>
                </EmptyHeader>
            </Empty>
        )
    }

    if (results !== null) {
        if (results.length === 0) {
            return (
                <Empty className="border border-dashed">
                    <EmptyHeader>
                        <EmptyMedia variant="icon"><SearchX /></EmptyMedia>
                        <EmptyTitle>No match for “{query.trim()}”</EmptyTitle>
                        <EmptyDescription>Try a different word, such as “gift”, “board” or “AI”.</EmptyDescription>
                    </EmptyHeader>
                    <EmptyContent>
                        <Button variant="outline" onClick={onClearQuery}>Show all forms</Button>
                    </EmptyContent>
                </Empty>
            )
        }
        return (
            <section aria-labelledby="results-heading" className="flex flex-col gap-3">
                <h2 id="results-heading" className="text-sm font-medium text-muted-foreground">
                    Best matches
                </h2>
                <ul className={grid} role="list">
                    {results.map((f, i) => (
                        <li key={f.id} className="flex">
                            <FormStartCard form={f} onStart={onStart} starting={starting} highlighted={i === 0} />
                        </li>
                    ))}
                </ul>
            </section>
        )
    }

    return (
        <div className="flex flex-col gap-10">
            {groupByCategory(forms).map((g) => {
                const CatIcon = ICONS[categoryIcon(g.category)]
                const id = `cat-${g.category || 'general'}`
                return (
                    <section key={g.category} aria-labelledby={id} className="flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            <CatIcon aria-hidden className="size-4 text-primary" />
                            <h2 id={id} className="text-base font-semibold tracking-tight">{g.label}</h2>
                            <span className="text-sm text-muted-foreground tabular-nums">
                                {g.forms.length} {g.forms.length === 1 ? 'form' : 'forms'}
                            </span>
                            <span aria-hidden className="h-px flex-1 bg-border" />
                        </div>
                        <ul className={grid} role="list">
                            {g.forms.map((f) => (
                                <li key={f.id} className="flex">
                                    <FormStartCard form={f} onStart={onStart} starting={starting} />
                                </li>
                            ))}
                        </ul>
                    </section>
                )
            })}
        </div>
    )
}
